import React from 'react'
import PropTypes from 'prop-types'
import { useParams, useHistory } from 'react-router-dom'
import { Trash2Icon, ToggleLeftIcon, ToggleRightIcon } from 'lucide-react'
import pluralize from 'pluralize'
import { Button } from '../../ui/button'
import Badge from '../../leevo_ui/Badge/Badge'

function ClassPackagesTable({ klassPackages, onEditClick, onDeleteClick, onToggleClick }) {
  const { facility_id: facilityId } = useParams()
  const history = useHistory()

  function formatPrice(price) {
    if (!price) return '$0.00'
    return `$${(price / 100).toFixed(2)}`
  }

  return (
    <>
      <div className="hidden overflow-x-auto lg:block">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                scope="col"
                className="px-4 py-2.5 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
              >
                Title
              </th>
              <th
                scope="col"
                className="px-4 py-2.5 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
              >
                Description
              </th>
              <th
                scope="col"
                className="px-4 py-2.5 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
              >
                Price
              </th>
              <th
                scope="col"
                className="px-4 py-2.5 text-xs font-medium tracking-wider text-center text-gray-500 uppercase"
              >
                Status
              </th>
              <th
                scope="col"
                className="px-4 py-2.5 text-xs font-medium tracking-wider text-center text-gray-500 uppercase"
              >
                Students
              </th>
              <th
                scope="col"
                className="px-4 py-2.5 text-xs font-medium tracking-wider text-right text-gray-500 uppercase"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {klassPackages.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-4 py-12 text-center text-gray-500">
                  No class packages found
                </td>
              </tr>
            ) : (
              klassPackages.map((pkg) => {
                return (
                  <tr key={pkg.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-gray-900">{pkg.title}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-500 max-w-xs truncate">
                        {pkg.description || 'No description'}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{formatPrice(pkg.price)}</div>
                    </td>
                    <td className="px-4 py-3 text-center whitespace-nowrap">
                      <Badge text={pkg.active ? 'Active' : 'Disabled'} color={pkg.active ? 'green' : 'gray'} />
                    </td>
                    <td className="px-4 py-3 text-center whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        <span className="font-semibold">{pkg.studentsCount || 0}</span>{' '}
                        {pluralize('student', pkg.studentsCount || 0)}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onEditClick(pkg.id)}
                          className="h-8"
                          data-cy="edit"
                        >
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onToggleClick(pkg)}
                          className="h-8"
                          data-cy="toggle"
                        >
                          {pkg.active ? (
                            <>
                              <ToggleLeftIcon className="w-3.5 h-3.5 mr-2" />
                              Disable
                            </>
                          ) : (
                            <>
                              <ToggleRightIcon className="w-3.5 h-3.5 mr-2" />
                              Enable
                            </>
                          )}
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => onDeleteClick(pkg)}
                          className="h-8"
                          data-cy="delete"
                        >
                          <Trash2Icon className="w-3.5 h-3.5 mr-2" />
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="p-3 space-y-3 lg:hidden">
        {klassPackages.length === 0 ? (
          <div className="px-6 py-12 text-center text-gray-500">No class packages found</div>
        ) : (
          klassPackages.map((pkg) => {
            return (
              <div key={pkg.id} className="p-3 bg-white border border-gray-200 rounded-lg" data-cy="klass-package-item">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="text-base font-semibold text-gray-900">{pkg.title}</h3>
                    <p className="text-sm text-gray-500 mt-1">{pkg.description || 'No description'}</p>
                  </div>
                  <Badge text={pkg.active ? 'Active' : 'Disabled'} color={pkg.active ? 'green' : 'gray'} />
                </div>

                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div>
                    <label className="block mb-1 text-xs font-medium text-gray-500 uppercase">Price</label>
                    <p className="text-sm font-medium text-gray-900">{formatPrice(pkg.price)}</p>
                  </div>

                  <div>
                    <label className="block mb-1 text-xs font-medium text-gray-500 uppercase">Students</label>
                    <p className="text-sm text-gray-900">
                      <span className="font-semibold">{pkg.studentsCount || 0}</span>{' '}
                      {pluralize('student', pkg.studentsCount || 0)}
                    </p>
                  </div>
                </div>

                <div className="flex flex-col gap-2 pt-3 mt-3 border-t border-gray-200">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onEditClick(pkg.id)}
                    className="w-full h-9"
                    data-cy="edit"
                  >
                    Edit
                  </Button>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onToggleClick(pkg)}
                      className="flex-1 h-9"
                      data-cy="toggle"
                    >
                      {pkg.active ? (
                        <>
                          <ToggleLeftIcon className="w-3.5 h-3.5 mr-2" />
                          Disable
                        </>
                      ) : (
                        <>
                          <ToggleRightIcon className="w-3.5 h-3.5 mr-2" />
                          Enable
                        </>
                      )}
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => onDeleteClick(pkg)}
                      className="flex-1 h-9"
                      data-cy="delete"
                    >
                      <Trash2Icon className="w-3.5 h-3.5 mr-2" />
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            )
          })
        )}
      </div>
    </>
  )
}

ClassPackagesTable.propTypes = {
  klassPackages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      title: PropTypes.string,
      description: PropTypes.string,
      price: PropTypes.number,
      active: PropTypes.bool,
      studentsCount: PropTypes.number
    })
  ).isRequired,
  onEditClick: PropTypes.func.isRequired,
  onDeleteClick: PropTypes.func.isRequired,
  onToggleClick: PropTypes.func.isRequired
}

export default ClassPackagesTable
